import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { SandboxArtifact, type ArtifactVersion, type SidePreviewItem } from "@/components/SandboxArtifact";

interface SandboxOutputRow {
  id: string;
  filename: string;
  storage_url: string;
  storage_path: string;
  mime_type: string | null;
  file_size_bytes: number | null;
  display_name: string | null;
  created_at: string;
}

interface Props {
  conversationId: string | undefined;
  userId: string | undefined;
  onPreview?: (item: SidePreviewItem) => void;
  // Filtra arquivos por janela temporal (atrelar ao turno do assistant).
  // Se omitido, mostra todos os arquivos da conversa (modo global legado).
  rangeStart?: string;       // ISO timestamp (inclusive)
  rangeEnd?: string;         // ISO timestamp (exclusive). Undefined = ate agora
  variant?: "global" | "inline"; // inline = compacto
}

function cleanFilename(filename: string, displayName?: string | null): string {
  if (displayName) return displayName;
  return filename.replace(/^\d{10,}-/, "");
}

function isHtmlRow(row: { filename: string; mime_type: string | null }): boolean {
  if (row.mime_type === "text/html") return true;
  const ext = row.filename.split(".").pop()?.toLowerCase() ?? "";
  return ext === "html" || ext === "htm";
}

function rowToVersion(row: SandboxOutputRow): ArtifactVersion {
  return {
    id: row.id,
    url: row.storage_url,
    filename: row.filename,
    mimeType: row.mime_type ?? undefined,
    sizeBytes: row.file_size_bytes ?? undefined,
    displayName: row.display_name ?? undefined,
    createdAt: row.created_at,
  };
}

export function SandboxArtifactsPanel({
  conversationId,
  userId,
  onPreview,
  rangeStart,
  rangeEnd,
  variant = "global",
}: Props) {
  const [items, setItems] = useState<SandboxOutputRow[]>([]);
  const [expanded, setExpanded] = useState(true);
  const knownNamesRef = useRef<Set<string>>(new Set());
  const initialLoadDoneRef = useRef(false);

  useEffect(() => {
    if (!conversationId || !userId) {
      setItems([]);
      knownNamesRef.current = new Set();
      initialLoadDoneRef.current = false;
      return;
    }
    let cancelled = false;
    knownNamesRef.current = new Set();
    initialLoadDoneRef.current = false;

    const load = async () => {
      const { data, error } = await supabase
        .from("sandbox_outputs")
        .select("id, filename, storage_url, storage_path, mime_type, file_size_bytes, display_name, created_at")
        .eq("conversation_id", conversationId)
        .eq("user_id", userId)
        .order("created_at", { ascending: false });
      if (!cancelled && !error && data) {
        const rows = data as SandboxOutputRow[];
        setItems(rows);
        if (!initialLoadDoneRef.current) {
          // Seed known semantic names with everything that already existed —
          // these should NOT trigger "nova versão" toasts on first load.
          for (const row of rows) {
            knownNamesRef.current.add(cleanFilename(row.filename, row.display_name));
          }
          initialLoadDoneRef.current = true;
        }
      }
    };

    load();

    const channel = supabase
      .channel(`sandbox_outputs:conversation_id=eq.${conversationId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "sandbox_outputs",
          filter: `conversation_id=eq.${conversationId}`,
        },
        (payload) => {
          load();
          if (payload.eventType === "INSERT") {
            const newRow = payload.new as SandboxOutputRow;
            if (!newRow) return;
            const semanticName = cleanFilename(newRow.filename, newRow.display_name);
            const isNewVersion = knownNamesRef.current.has(semanticName);
            knownNamesRef.current.add(semanticName);

            // Auto-preview de HTML quando aparece (continua so HTML —
            // pra .md o user clica no card, evita abrir painel a cada
            // arquivo de docs gerado).
            if (isHtmlRow(newRow) && onPreview) {
              onPreview({
                url: newRow.storage_url,
                name: semanticName,
                kind: 'html',
              });
            }

            if (isNewVersion) {
              toast.success(`Nova versão de ${semanticName} disponível`);
            }
          }
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [conversationId, userId, onPreview]);

  // Filtra items por janela temporal quando em modo inline (atrelado a turno).
  const filteredItems = useMemo(() => {
    if (!rangeStart && !rangeEnd) return items;
    const startMs = rangeStart ? new Date(rangeStart).getTime() : -Infinity;
    const endMs = rangeEnd ? new Date(rangeEnd).getTime() : Infinity;
    return items.filter((it) => {
      const t = new Date(it.created_at).getTime();
      return t >= startMs && t < endMs;
    });
  }, [items, rangeStart, rangeEnd]);

  // Agrupa por nome semântico — items já vem ordenado desc por created_at,
  // então cada grupo fica [mais_novo, ..., mais_antigo].
  const groups = useMemo(() => {
    const map = new Map<string, SandboxOutputRow[]>();
    for (const item of filteredItems) {
      const key = cleanFilename(item.filename, item.display_name);
      if (!map.has(key)) map.set(key, []);
      map.get(key)!.push(item);
    }
    return Array.from(map.values());
  }, [filteredItems]);

  if (!conversationId || filteredItems.length === 0) return null;

  const groupCount = groups.length;
  const totalCount = filteredItems.length;
  const hasOldVersions = totalCount > groupCount;
  const inline = variant === "inline";
  const headerLabel = inline
    ? `${groupCount} ${groupCount === 1 ? "arquivo gerado" : "arquivos gerados"}${hasOldVersions ? ` (${totalCount} ${totalCount === 1 ? "versão" : "versões"})` : ""}`
    : hasOldVersions
      ? `${groupCount} ${groupCount === 1 ? "arquivo gerado" : "arquivos gerados"} (${totalCount} ${totalCount === 1 ? "versão" : "versões"})`
      : `${groupCount} ${groupCount === 1 ? "arquivo gerado" : "arquivos gerados"} nesta conversa`;

  return (
    <div className={`${inline ? "mt-2" : "mb-4"} rounded-lg border border-color bg-context overflow-hidden`}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-medium text-secondary hover:bg-hover transition-colors"
      >
        <span className="inline-flex items-center gap-2">
          <Paperclip className="w-3.5 h-3.5" />
          {headerLabel}
        </span>
        {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {expanded && (
        <div className={`px-3 py-3 ${inline ? "" : "max-h-80 overflow-y-auto"} border-t border-color`}>
          <div className="grid grid-cols-2 gap-2">
            {groups.map((groupRows) => {
              const latest = groupRows[0];
              return (
                <SandboxArtifact
                  key={latest.id}
                  filename={latest.filename}
                  url={latest.storage_url}
                  mimeType={latest.mime_type ?? undefined}
                  sizeBytes={latest.file_size_bytes ?? undefined}
                  displayName={latest.display_name ?? undefined}
                  onPreview={onPreview}
                  versions={groupRows.map(rowToVersion)}
                />
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default SandboxArtifactsPanel;
